import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import languagePreferenceManager from '../lib/utils/languagePreference';

// Import all EN locale files
import enAccount from '../common/locales/en/account.json';
import enAddress from '../common/locales/en/address.json';
import enAmenity from '../common/locales/en/amenity.json';
import enArticle from '../common/locales/en/article.json';
import enBadge from '../common/locales/en/badge.json';
import enBrand from '../common/locales/en/brand.json';
import enContests from '../common/locales/en/contests.json';
import enDates from '../common/locales/en/dates.json';
import enEmail from '../common/locales/en/email.json';
import enFavorites from '../common/locales/en/favorites.json';
import enFeature from '../common/locales/en/feature.json';
import enFuel from '../common/locales/en/fuel.json';
import enGasbackOffers from '../common/locales/en/gasbackOffers.json';
import enLeaderboard from '../common/locales/en/leaderboard.json';
import enLegal from '../common/locales/en/legal.json';
import enLocation from '../common/locales/en/location.json';
import enMarketing from '../common/locales/en/marketing.json';
import enMiscellaneous from '../common/locales/en/miscellaneous.json';
import enMSL from '../common/locales/en/msl.json';
import enPay from '../common/locales/en/pay.json';
import enPrice from '../common/locales/en/price.json';
import enRatingCategories from '../common/locales/en/ratingCategories.json';
import enRecalls from '../common/locales/en/recalls.json';
import enReview from '../common/locales/en/review.json';
import enStation from '../common/locales/en/station.json';
import enTestimonials from '../common/locales/en/testimonials.json';
import enTimeline from '../common/locales/en/timeline.json';
import enTrend from '../common/locales/en/trend.json';
import enTrip from '../common/locales/en/trip.json';
import enVehicle from '../common/locales/en/vehicle.json';

// Import all FR locale files
import frAccount from '../common/locales/fr/account.json';
import frAddress from '../common/locales/fr/address.json';
import frAmenity from '../common/locales/fr/amenity.json';
import frArticle from '../common/locales/fr/article.json';
import frBadge from '../common/locales/fr/badge.json';
import frBrand from '../common/locales/fr/brand.json';
import frContests from '../common/locales/fr/contests.json';
import frDates from '../common/locales/fr/dates.json';
import frEmail from '../common/locales/fr/email.json';
import frFavorites from '../common/locales/fr/favorites.json';
import frFeature from '../common/locales/fr/feature.json';
import frFuel from '../common/locales/fr/fuel.json';
import frGasbackOffers from '../common/locales/fr/gasbackOffers.json';
import frLeaderboard from '../common/locales/fr/leaderboard.json';
import frLegal from '../common/locales/fr/legal.json';
import frLocation from '../common/locales/fr/location.json';
import frMarketing from '../common/locales/fr/marketing.json';
import frMiscellaneous from '../common/locales/fr/miscellaneous.json';
import frMSL from '../common/locales/fr/msl.json';
import frPay from '../common/locales/fr/pay.json';
import frPrice from '../common/locales/fr/price.json';
import frRatingCategories from '../common/locales/fr/ratingCategories.json';
import frRecalls from '../common/locales/fr/recalls.json';
import frReview from '../common/locales/fr/review.json';
import frStation from '../common/locales/fr/station.json';
import frTestimonials from '../common/locales/fr/testimonials.json';
import frTimeline from '../common/locales/fr/timeline.json';
import frTrend from '../common/locales/fr/trend.json';
import frTrip from '../common/locales/fr/trip.json';
import frVehicle from '../common/locales/fr/vehicle.json';

const EN_RESOURCES = {
  account: enAccount,
  address: enAddress,
  amenity: enAmenity,
  article: enArticle,
  badge: enBadge,
  brand: enBrand,
  contests: enContests,
  dates: enDates,
  email: enEmail,
  favorites: enFavorites,
  feature: enFeature,
  fuel: enFuel,
  gasbackOffers: enGasbackOffers,
  leaderboard: enLeaderboard,
  legal: enLegal,
  location: enLocation,
  marketing: enMarketing,
  miscellaneous: enMiscellaneous,
  msl: enMSL,
  pay: enPay,
  price: enPrice,
  ratingCategories: enRatingCategories,
  recalls: enRecalls,
  review: enReview,
  station: enStation,
  testimonials: enTestimonials,
  timeline: enTimeline,
  trend: enTrend,
  trip: enTrip,
  vehicle: enVehicle,
};

const FR_RESOURCES = {
  account: frAccount,
  address: frAddress,
  amenity: frAmenity,
  article: frArticle,
  badge: frBadge,
  brand: frBrand,
  contests: frContests,
  dates: frDates,
  email: frEmail,
  favorites: frFavorites,
  feature: frFeature,
  fuel: frFuel,
  gasbackOffers: frGasbackOffers,
  leaderboard: frLeaderboard,
  legal: frLegal,
  location: frLocation,
  marketing: frMarketing,
  miscellaneous: frMiscellaneous,
  msl: frMSL,
  pay: frPay,
  price: frPrice,
  ratingCategories: frRatingCategories,
  recalls: frRecalls,
  review: frReview,
  station: frStation,
  testimonials: frTestimonials,
  timeline: frTimeline,
  trend: frTrend,
  trip: frTrip,
  vehicle: frVehicle,
};

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: EN_RESOURCES,
      fr: FR_RESOURCES,
    },
    fallbackLng: 'en', // we should always have en translations as that is our default language
    lng: languagePreferenceManager.getStoredLanguagePreference(), // Get language from localStorage to persist user preference across reloads
    debug: process.env.NODE_ENV === 'development',
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    ns: Object.keys(EN_RESOURCES), // Load all namespaces
    defaultNS: 'account', // Set account as default namespace
    react: {
      useSuspense: false,
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p'],
    },
  });

// Listen for language changes and sync to cookie for server-side access
i18n.on('languageChanged', (language) => {
  // Use centralized language preference manager
  languagePreferenceManager.setLanguagePreference(language);
});

export default i18n;
